import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { FormInstance } from 'antd';
import { useMemo } from 'react';

import type { HotelDiscoverExperience } from 'app/typings/hotels/Hotel/HotelDiscover';

type UseExperienceListLogicProps = {
  experiences: HotelDiscoverExperience[];
  form: FormInstance;
  setIsDirtyMove: (value: boolean) => void;
};

export const useExperienceListLogic = ({
  experiences,
  form,
  setIsDirtyMove,
}: UseExperienceListLogicProps) => {
  const { alwaysIncludedExperiences, otherExperiences } = useMemo(() => {
    return experiences.reduce(
      (
        acc: {
          alwaysIncludedExperiences: HotelDiscoverExperience[];
          otherExperiences: HotelDiscoverExperience[];
        },
        exp
      ) => {
        if (exp.isAlwaysIncludedWithNight) {
          acc.alwaysIncludedExperiences.push(exp);
        } else {
          acc.otherExperiences.push(exp);
        }

        return acc;
      },
      { alwaysIncludedExperiences: [], otherExperiences: [] }
    );
  }, [experiences]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!active || !over || active.id === over.id) {
      return;
    }

    setIsDirtyMove(true);

    const activeIndex = experiences.findIndex((i) => i.id === active.id);
    const overIndex = experiences.findIndex((i) => i.id === over.id);

    const newArray = arrayMove(experiences, activeIndex, overIndex);

    form.setFieldValue(
      'experiences',
      newArray.map((exp, index) => ({ ...exp, discoverPriority: index }))
    );
  };

  const createToggleExperienceHandler = (
    experience: HotelDiscoverExperience
  ) => {
    return (value: boolean) => {
      const current: HotelDiscoverExperience[] =
        form.getFieldValue('experiences') || [];

      const next = current.map((exp) =>
        exp.id === experience.id ? { ...exp, discover: value } : exp
      );

      form.setFieldsValue({ experiences: next });
      setIsDirtyMove(true);
    };
  };

  return {
    alwaysIncludedExperiences,
    otherExperiences,
    onDragEnd,
    createToggleExperienceHandler,
  };
};
